import { useMemo } from "react";
import { searchHighlightTermList } from "@/lib/searchHighlightTerms";
import { cn } from "@/lib/utils";

interface HighlightedTextProps {
  text: string;
  /** The search as typed; quoted phrases match whole, `-negated` words are never marked. */
  searchTerm: string | undefined;
  className?: string;
  markClassName?: string;
}

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * One regex matching any of the terms, case-insensitively. Longer terms first so a phrase
 * wins over a word it contains (searchHighlightTermList already puts phrases first).
 */
const buildMatcher = (terms: string[]): RegExp | null => {
  if (terms.length === 0) return null;
  const alternatives = [...terms]
    .map((term) => term.normalize("NFC"))
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join("|");
  return new RegExp(`(${alternatives})`, "gi");
};

/**
 * Renders `text` with every occurrence of the search terms wrapped in a `<mark>`, using
 * the same term parsing as the in-document search so a list row and the opened document
 * highlight the same words (LAR-235, LAR-264). Text and terms are compared in composed
 * Unicode (NFC): file names from macOS and SharePoint arrive decomposed ('å' as 'a' +
 * U+030A) and would otherwise never match what the user typed. The rendered text is the
 * composed form, which reads identically.
 */
export const HighlightedText = ({
  text,
  searchTerm,
  className,
  markClassName,
}: HighlightedTextProps) => {
  const matcher = useMemo(
    () => buildMatcher(searchHighlightTermList(searchTerm)),
    [searchTerm]
  );
  const composed = text.normalize("NFC");

  if (!matcher) {
    return <span className={className}>{composed}</span>;
  }

  // split() with a capturing group keeps the matches at the odd indexes.
  const parts = composed.split(matcher);

  return (
    <span className={className}>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <mark
            key={index}
            className={cn(
              "rounded-xs bg-[var(--doc-highlight-search-bg)] px-px text-inherit",
              markClassName
            )}
          >
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </span>
  );
};

export default HighlightedText;
